import React, { createContext, useContext, useState, useMemo, useCallback, ReactNode } from 'react';
import {
  DEFAULT_AUTO_CLOSE_DELAY_MS,
  feedbackConfig,
  FeedbackDialogContextType,
  FeedbackState,
  FeedbackType,
} from '../../components/FeedbackDialog/FeedbackDialog.types';

export const FeedbackDialogProvider = ({ children }: { children: ReactNode }) => {
  const [state, setState] = useState<FeedbackState>({
    open: false,
    message: '',
    type: FeedbackType.SUCCESS,
    autoCloseDelay: DEFAULT_AUTO_CLOSE_DELAY_MS,
  });

  const { open, message, title, type, autoCloseDelay } = state;

  const handleDialogVisibility = useCallback((isOpen: boolean) => {
    setState((prevState) => ({
      ...prevState,
      open: isOpen,
    }));
  }, []);

  const showFeedbackDialog = useCallback(
    (
      message: string,
      type: FeedbackType,
      autoCloseDelay: number = DEFAULT_AUTO_CLOSE_DELAY_MS,
      title?: string,
    ) => {
      setState({
        open: true,
        message,
        title,
        type,
        autoCloseDelay,
      });
    },
    [],
  );

  const value = useMemo<FeedbackDialogContextType>(
    () => ({
      open,
      message,
      title,
      type,
      config: feedbackConfig[type],
      autoCloseDelay,
      handleDialogVisibility,
      showFeedbackDialog,
    }),
    [open, message, title, type, autoCloseDelay, handleDialogVisibility, showFeedbackDialog],
  );

  return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
};

const FeedbackContext = createContext<FeedbackDialogContextType | undefined>(undefined);

export const useFeedbackDialog = () => {
  const context = useContext(FeedbackContext);
  if (!context) throw new Error('useFeedbackDialog must be used within FeedbackDialogProvider');
  return context;
};
